'use client';
import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import {
  KanbanCard,
  CardPriority,
  CardStatus,
  KANBAN_MEMBERS,
  KANBAN_LABELS,
  KANBAN_COLUMNS,
} from './kanbanMockData';

interface CardDetailSheetProps {
  card: KanbanCard;
  onClose: () => void;
  onUpdate: (card: KanbanCard) => void;
  onDelete: (cardId: string) => void;
}

interface CardFormData {
  title: string;
  description: string;
  status: CardStatus;
  priority: CardPriority;
  assigneeId: string;
  storyPoints: number;
  dueDate: string;
}

const statusVariantMap: Record<CardStatus, 'active' | 'completed' | 'on-hold' | 'archived' | 'info'> = {
  Backlog: 'archived',
  'To Do': 'info',
  'In Progress': 'on-hold',
  'In Review': 'active',
  Done: 'completed',
};

export default function CardDetailSheet({ card, onClose, onUpdate, onDelete }: CardDetailSheetProps) {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState<string[]>(card.labels.map((l) => l.id));
  const overlayRef = useRef<HTMLDivElement>(null);

  const { register, handleSubmit, formState: { errors, isDirty }, reset } = useForm<CardFormData>({
    defaultValues: {
      title: card.title,
      description: card.description,
      status: card.status,
      priority: card.priority,
      assigneeId: card.assignee?.id ?? '',
      storyPoints: card.storyPoints,
      dueDate: card.dueDate,
    },
  });

  useEffect(() => {
    reset({
      title: card.title,
      description: card.description,
      status: card.status,
      priority: card.priority,
      assigneeId: card.assignee?.id ?? '',
      storyPoints: card.storyPoints,
      dueDate: card.dueDate,
    });
    setSelectedLabels(card.labels.map((l) => l.id));
  }, [card.id, reset]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const toggleLabel = (labelId: string) => {
    setSelectedLabels((prev) =>
      prev.includes(labelId) ? prev.filter((l) => l !== labelId) : [...prev, labelId]
    );
  };

  const onSubmit = async (data: CardFormData) => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 600));
    const assignee = KANBAN_MEMBERS.find((m) => m.id === data.assigneeId) ?? null;
    const labels = KANBAN_LABELS.filter((l) => selectedLabels.includes(l.id));
    const targetCol = KANBAN_COLUMNS.find((c) => c.title === data.status);
    onUpdate({
      ...card,
      title: data.title,
      description: data.description,
      status: data.status,
      priority: data.priority,
      assignee,
      storyPoints: data.storyPoints,
      dueDate: data.dueDate,
      labels,
      columnId: targetCol?.id ?? card.columnId,
    });
    setSaving(false);
  };

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-40 flex items-end sm:items-center justify-end"
        style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }}
        onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      >
        {/* Sheet */}
        <div
          className="slide-in-bottom sm:scale-in w-full sm:w-[480px] h-[92vh] sm:h-full card-base sm:rounded-l-2xl sm:rounded-r-none rounded-t-2xl flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Card detail"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
            <div className="flex items-center gap-2">
              <Badge variant={statusVariantMap[card.status]} dot>
                {card.status}
              </Badge>
              {card.overdue && (
                <span className="flex items-center gap-1 text-xs text-danger">
                  <Icon name="ExclamationTriangleIcon" size={12} />
                  Overdue
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="p-1.5 rounded-lg hover:bg-danger/10 transition-colors"
                aria-label="Delete card"
              >
                <Icon name="TrashIcon" size={15} className="text-danger" />
              </button>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors" aria-label="Close">
                <Icon name="XMarkIcon" size={18} className="text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Form body */}
          <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 scrollbar-hide">
            {/* Title */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1" htmlFor="card-title">
                TITLE
              </label>
              <input
                id="card-title"
                type="text"
                className="input-field text-base font-semibold"
                {...register('title', { required: 'Title is required' })}
              />
              {errors.title && <p className="mt-1 text-xs text-danger">{errors.title.message}</p>}
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1" htmlFor="card-desc">
                DESCRIPTION
              </label>
              <textarea
                id="card-desc"
                rows={4}
                placeholder="Add more context…"
                className="input-field resize-none text-sm"
                {...register('description')}
              />
            </div>

            {/* Status + Priority row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1" htmlFor="card-status">
                  STATUS
                </label>
                <select id="card-status" className="input-field text-sm" {...register('status')}>
                  <option value="Backlog">Backlog</option>
                  <option value="To Do">To Do</option>
                  <option value="In Progress">In Progress</option>
                  <option value="In Review">In Review</option>
                  <option value="Done">Done</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1" htmlFor="card-priority">
                  PRIORITY
                </label>
                <select id="card-priority" className="input-field text-sm" {...register('priority')}>
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>

            {/* Assignee + Story Points row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1" htmlFor="card-assignee">
                  ASSIGNEE
                </label>
                <select id="card-assignee" className="input-field text-sm" {...register('assigneeId')}>
                  <option value="">Unassigned</option>
                  {KANBAN_MEMBERS.map((m) => (
                    <option key={`assignee-opt-${m.id}`} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1" htmlFor="card-sp">
                  STORY POINTS
                </label>
                <input
                  id="card-sp"
                  type="number"
                  min="0"
                  max="100"
                  className="input-field text-sm font-tabular"
                  {...register('storyPoints', { valueAsNumber: true, min: { value: 0, message: 'Min 0' }, max: { value: 100, message: 'Max 100' } })}
                />
              </div>
            </div>

            {/* Due date */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1" htmlFor="card-due">
                DUE DATE
              </label>
              <input
                id="card-due"
                type="date"
                className="input-field text-sm"
                {...register('dueDate')}
              />
            </div>

            {/* Labels */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-2">
                LABELS
              </label>
              <div className="flex flex-wrap gap-1.5">
                {KANBAN_LABELS.map((label) => {
                  const active = selectedLabels.includes(label.id);
                  return (
                    <button
                      key={`label-toggle-${label.id}`}
                      type="button"
                      onClick={() => toggleLabel(label.id)}
                      className="px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150"
                      style={{
                        background: active ? label.color + '30' : 'var(--secondary)',
                        color: active ? label.color : 'var(--muted-foreground)',
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: active ? label.color + '60' : 'transparent',
                      }}
                    >
                      {label.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-border flex items-center justify-between gap-3 flex-shrink-0">
            <p className="text-xs text-muted-foreground">
              {isDirty ? 'Unsaved changes' : 'No changes'}
            </p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose} className="btn-ghost h-9">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit(onSubmit)}
                disabled={saving || !isDirty}
                className="btn-primary h-9"
              >
                {saving ? (
                  <>
                    <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                    Saving…
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirm */}
      <Modal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete Card"
        size="sm"
        footer={
          <>
            <button onClick={() => setDeleteConfirmOpen(false)} className="btn-ghost">Cancel</button>
            <button
              onClick={() => { onDelete(card.id); setDeleteConfirmOpen(false); }}
              className="btn-danger"
            >
              <Icon name="TrashIcon" size={14} />
              Delete Card
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-foreground">
            Delete <span className="font-semibold">"{card.title}"</span>?
          </p>
          <p className="text-xs text-muted-foreground">
            This card and all its data will be permanently removed. This cannot be undone.
          </p>
        </div>
      </Modal>
    </>
  );
}